/**
 * Login page: sign-in, invitation setup, and password reset.
 */
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmationResult } from 'firebase/auth';
import { AuthCard } from '@/components/auth/AuthCard';
import {
  EMPTY_SETUP_PASSWORD,
  LOGIN_PARTICLE_INDICES,
  PHONE_SETUP_OTP_COUNTDOWN_S,
  type LoginView,
  type ResetState,
  type SetupPasswordData,
} from '@/components/auth/auth-configs';
import {
  loginParticleClass,
  patchSetupPassword,
  resolveThemeLogo,
} from '@/components/auth/auth-utils';
import { LoginForm } from '@/components/auth/LoginForm';
import { PhoneSetupForm } from '@/components/auth/PhoneSetupForm';
import { ResetPasswordForm } from '@/components/auth/ResetPasswordForm';
import { SetupPasswordForm } from '@/components/auth/SetupPasswordForm';
import { OtpModal } from '@/components/common/OtpModal';
import { toast, Toaster } from '@/components/ui/toast';
import { useHubSpotScript } from '@/hooks/useHubSpotScript';
import { useInvitationSetup } from '@/hooks/useInvitationSetup';
import { useLogin } from '@/hooks/useLogin';
import { sendPasswordResetEmail } from '@/services/auth.service';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';
import { isValidEmail } from '@/utils/auth-validation-utils';
import { getErrorMessage } from '@/utils/errors-utils';

/** Login, invitation setup, phone OTP, and forgot-password on one page. */
export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { logos, isDarkMode } = useThemeStore();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const {
    isSetupMode,
    isEmailSetup,
    isPhoneSetup,
    isActivateSetup,
    isResetMode,
    phone: invitePhone,
    invitationEmail,
    setupPassword,
    activateAccount,
    resetPassword,
    verifyPhoneSetup,
    sendPhoneSetupOtp,
    isLoading: setupLoading,
    error: setupError,
  } = useInvitationSetup();

  const [view, setView] = React.useState<LoginView>('login');

  useHubSpotScript(view === 'login' && !isSetupMode);

  const [identifierTouched, setIdentifierTouched] = React.useState(false);
  const [passwordTouched, setPasswordTouched] = React.useState(false);
  const [resetEmailTouched, setResetEmailTouched] = React.useState(false);
  const [setupEmailTouched, setSetupEmailTouched] = React.useState(false);
  const [setupPasswordTouched, setSetupPasswordTouched] = React.useState(false);
  const [setupConfirmPasswordTouched, setSetupConfirmPasswordTouched] = React.useState(false);

  const {
    mode,
    identifier,
    password,
    rememberMe,
    isLoading,
    setPassword,
    setRememberMe,
    handleIdentifierChange,
    switchToPhone,
    handleEmailLogin,
    handleSendOtp,
    handleVerifyOtp,
  } = useLogin();

  const [showOtpModal, setShowOtpModal] = React.useState(false);
  const [confirmationResult, setConfirmationResult] = React.useState<ConfirmationResult | null>(
    null,
  );

  const [resetEmail, setResetEmail] = React.useState('');
  const [resetState, setResetState] = React.useState<ResetState>('initial');
  const [resetError, setResetError] = React.useState<string | undefined>();

  const [setupPasswordData, setSetupPasswordData] =
    React.useState<SetupPasswordData>(EMPTY_SETUP_PASSWORD);

  const [otpValue, setOtpValue] = React.useState('');
  const [phoneConfirmationResult, setPhoneConfirmationResult] =
    React.useState<ConfirmationResult | null>(null);
  const [countdown, setCountdown] = React.useState(PHONE_SETUP_OTP_COUNTDOWN_S);

  /** Invitation links open password-setup or phone-setup instead of the login form. */
  React.useEffect(() => {
    if (isEmailSetup || isActivateSetup || isResetMode) {
      setView('setup-password');
      if (invitationEmail) {
        setSetupPasswordData((prev) => patchSetupPassword(prev, 'email', invitationEmail));
      }
    } else if (isPhoneSetup) {
      setView('phone-setup');
    }
  }, [isEmailSetup, isActivateSetup, isResetMode, isPhoneSetup, invitationEmail]);

  /** Already-signed-in users skip the form unless they are finishing an invitation. */
  React.useEffect(() => {
    if (isAuthenticated && !isSetupMode) {
      void navigate('/dashboard');
    }
  }, [isAuthenticated, isSetupMode, navigate]);

  /** Send the first phone-setup OTP once the view and invite number are ready. */
  React.useEffect(() => {
    if (view === 'phone-setup' && invitePhone && !phoneConfirmationResult) {
      sendPhoneSetupOtp(invitePhone)
        .then(setPhoneConfirmationResult)
        .catch(() => {});
    }
  }, [view, invitePhone, phoneConfirmationResult]);

  /** Resend countdown while the phone-setup card is visible. */
  React.useEffect(() => {
    if (view === 'phone-setup' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [view, countdown]);

  /** Email/password sign-in, or send an OTP and open the verify modal. */
  async function handleSubmit(e: React.SubmitEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();

    if (mode === 'email') {
      await handleEmailLogin();
    } else if (mode === 'phone') {
      const result = await handleSendOtp();
      setConfirmationResult(result);
      setShowOtpModal(true);
    }
  }

  /** Complete phone login with the SMS code from {@link OtpModal}. */
  async function handleOtpVerify(code: string): Promise<void> {
    if (!confirmationResult) return;

    try {
      await handleVerifyOtp(confirmationResult, code);
      setShowOtpModal(false);
    } catch (err) {
      toast.error(getErrorMessage(err));
      throw err;
    }
  }

  /** Resend the login OTP and replace the pending confirmation. */
  async function handleOtpResend(): Promise<void> {
    try {
      const result = await handleSendOtp();
      setConfirmationResult(result);
    } catch (err) {
      toast.error(getErrorMessage(err));
      throw err;
    }
  }

  /** Validate the reset email and send Firebase’s password-reset mail. */
  async function handleResetSubmit(e: React.SubmitEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setResetError(undefined);

    if (!resetEmail.trim()) {
      setResetError(getErrorMessage('emailRequired'));
      return;
    }

    if (!isValidEmail(resetEmail)) {
      setResetError(getErrorMessage('invalidEmail'));
      return;
    }

    setResetState('sending');

    try {
      await sendPasswordResetEmail(resetEmail);
      toast.success(
        'Si un compte est associé à cette adresse, un email vous a été envoyé. Si vous venez d’être invité, ouvrez le bouton « Activer mon compte ».',
      );
      setResetEmail('');
      setResetState('initial');
      setView('login');
      setResetError(undefined);
    } catch (err) {
      setResetError(getErrorMessage(err));
      setResetState('initial');
    }
  }

  /** Leave the reset form and clear its local error / email state. */
  function handleBackToLogin(): void {
    setView('login');
    setResetEmail('');
    setResetState('initial');
    setResetError(undefined);
  }

  /**
   * Invitation password setup. `mode=activate` (14-day JWT) only needs the password;
   * email-invite setup also validates the address.
   * `mode=reset-password` handles password reset via Firebase oobCode.
   */
  async function handleSetupPassword(e: React.SubmitEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();

    if (isResetMode) {
      await resetPassword(setupPasswordData.password, setupPasswordData.confirmPassword);
      return;
    }

    if (isActivateSetup) {
      await activateAccount(setupPasswordData.password, setupPasswordData.confirmPassword);
      return;
    }

    if (!setupPasswordData.email) {
      toast.error(getErrorMessage('emailRequired'));
      return;
    }

    if (!isValidEmail(setupPasswordData.email)) {
      toast.error(getErrorMessage('invalidEmail'));
      return;
    }

    await setupPassword(
      setupPasswordData.email,
      setupPasswordData.password,
      setupPasswordData.confirmPassword,
    );
  }

  /** Confirm the invitation phone with the 6-digit SMS code. */
  async function handlePhoneSetupVerify(code: string): Promise<void> {
    if (!phoneConfirmationResult) {
      toast.error(getErrorMessage('noPendingConfirmation'));
      return;
    }

    await verifyPhoneSetup(phoneConfirmationResult, code);
  }

  /** Restart the resend countdown and send a new phone-setup OTP. */
  async function handleResendPhoneSetupOtp(): Promise<void> {
    if (!invitePhone) return;

    setCountdown(PHONE_SETUP_OTP_COUNTDOWN_S);
    const result = await sendPhoneSetupOtp(invitePhone);
    setPhoneConfirmationResult(result);
    toast.success('Code renvoyé avec succès !');
  }

  function handleLoginIdentifierChange(value: string): void {
    handleIdentifierChange(value);
    setIdentifierTouched(true);
  }

  function handleLoginPasswordChange(value: string): void {
    setPassword(value);
    setPasswordTouched(true);
  }

  function handleIdentifierTouched(): void {
    setIdentifierTouched(true);
  }

  function handlePasswordTouched(): void {
    setPasswordTouched(true);
  }

  function handleForgotPassword(): void {
    setView('reset-password');
  }

  function handleResetEmailChange(value: string): void {
    setResetEmail(value);
    setResetError(undefined);
    setResetEmailTouched(false);
  }

  function handleResetEmailBlur(): void {
    setResetEmailTouched(true);
  }

  function handleSetupEmailChange(value: string): void {
    setSetupPasswordData((prev) => patchSetupPassword(prev, 'email', value));
    setSetupEmailTouched(true);
  }

  function handleSetupPasswordChange(value: string): void {
    setSetupPasswordData((prev) => patchSetupPassword(prev, 'password', value));
    setSetupPasswordTouched(true);
  }

  function handleSetupConfirmPasswordChange(value: string): void {
    setSetupPasswordData((prev) => patchSetupPassword(prev, 'confirmPassword', value));
    setSetupConfirmPasswordTouched(true);
  }

  function handleSetupEmailBlur(): void {
    setSetupEmailTouched(true);
  }

  function handleSetupPasswordBlur(): void {
    setSetupPasswordTouched(true);
  }

  function handleSetupConfirmPasswordBlur(): void {
    setSetupConfirmPasswordTouched(true);
  }

  function handlePhoneSetupActivate(): void {
    void handlePhoneSetupVerify(otpValue);
  }

  function handleCloseOtpModal(): void {
    setShowOtpModal(false);
  }

  function renderLoginParticle(index: number): React.ReactNode {
    return <div key={`particle-${index}`} className={loginParticleClass(index)} />;
  }

  function renderLoginView(): React.ReactNode {
    if (view !== 'login') return null;
    return (
      <LoginForm
        logo={logo}
        mode={mode}
        identifier={identifier}
        password={password}
        rememberMe={rememberMe}
        isLoading={isLoading}
        identifierTouched={identifierTouched}
        passwordTouched={passwordTouched}
        onIdentifierChange={handleLoginIdentifierChange}
        onPasswordChange={handleLoginPasswordChange}
        onRememberMeChange={setRememberMe}
        onIdentifierBlur={handleIdentifierTouched}
        onPasswordBlur={handlePasswordTouched}
        onSubmit={handleSubmit}
        onForgotPassword={handleForgotPassword}
        onSwitchToPhone={switchToPhone}
      />
    );
  }

  function renderResetView(): React.ReactNode {
    if (view !== 'reset-password') return null;
    return (
      <ResetPasswordForm
        email={resetEmail}
        isLoading={resetState === 'sending'}
        emailTouched={resetEmailTouched}
        error={resetError}
        onEmailChange={handleResetEmailChange}
        onEmailBlur={handleResetEmailBlur}
        onSubmit={handleResetSubmit}
        onBackToLogin={handleBackToLogin}
      />
    );
  }

  function renderSetupView(): React.ReactNode {
    if (view !== 'setup-password') return null;
    return (
      <SetupPasswordForm
        logo={logo}
        email={setupPasswordData.email}
        password={setupPasswordData.password}
        confirmPassword={setupPasswordData.confirmPassword}
        emailTouched={setupEmailTouched}
        passwordTouched={setupPasswordTouched}
        confirmPasswordTouched={setupConfirmPasswordTouched}
        isLoading={setupLoading}
        error={setupError}
        isEmailDisabled={!!invitationEmail}
        mode={isResetMode ? 'reset' : 'activate'}
        onEmailChange={handleSetupEmailChange}
        onPasswordChange={handleSetupPasswordChange}
        onConfirmPasswordChange={handleSetupConfirmPasswordChange}
        onEmailBlur={handleSetupEmailBlur}
        onPasswordBlur={handleSetupPasswordBlur}
        onConfirmPasswordBlur={handleSetupConfirmPasswordBlur}
        onSubmit={handleSetupPassword}
      />
    );
  }

  function renderPhoneView(): React.ReactNode {
    if (view !== 'phone-setup') return null;
    return (
      <PhoneSetupForm
        logo={logo}
        phone={invitePhone || ''}
        otpValue={otpValue}
        countdown={countdown}
        isLoading={setupLoading}
        error={setupError}
        onOtpChange={setOtpValue}
        onOtpComplete={handlePhoneSetupVerify}
        onResendOtp={handleResendPhoneSetupOtp}
        onVerify={handlePhoneSetupActivate}
      />
    );
  }

  function renderOtpModal(): React.ReactNode {
    if (!showOtpModal) return null;
    return (
      <OtpModal
        open={showOtpModal}
        onClose={handleCloseOtpModal}
        onVerify={handleOtpVerify}
        onResend={handleOtpResend}
        phoneNumber={identifier}
      />
    );
  }

  const logo = React.useMemo(() => resolveThemeLogo(logos, isDarkMode), [isDarkMode, logos]);

  return (
    <>
      <div className="login-container">
        <div className="login-background">
          <div className="login-orb-small login-orb-color-2 login-orb-1" />
          <div className="login-orb-small login-orb-color-2 login-orb-2" />
          <div className="login-orb-small login-orb-color-2 login-orb-3" />
          <div className="login-orb-small login-orb-color-2 login-orb-4" />
          <div className="login-orb-small login-orb-color-2 login-orb-5" />

          <div className="login-orb-small login-orb-color-1 login-orb-6" />
          <div className="login-orb-small login-orb-color-1 login-orb-7" />
          <div className="login-orb-small login-orb-color-1 login-orb-8" />
          <div className="login-orb-small login-orb-color-1 login-orb-9" />

          <div className="login-orb-small login-orb-color-3 login-orb-10" />
          <div className="login-orb-small login-orb-color-3 login-orb-11" />
          <div className="login-orb-small login-orb-color-3 login-orb-12" />
          <div className="login-orb-small login-orb-color-3 login-orb-13" />
        </div>

        <div className="login-fullscreen-container">
          <div className="login-background-elements">
            <div className="floating-particles">
              {LOGIN_PARTICLE_INDICES.map(renderLoginParticle)}
            </div>

            <svg
              className="animated-wave wave-1"
              viewBox="0 0 1200 400"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <path d="M0,200 Q300,100 600,200 T1200,200 L1200,400 L0,400 Z" />
            </svg>
            <svg
              className="animated-wave wave-2"
              viewBox="0 0 1200 400"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <path d="M0,250 Q300,150 600,250 T1200,250 L1200,400 L0,400 Z" />
            </svg>
            <svg
              className="animated-wave wave-3"
              viewBox="0 0 1200 400"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <path d="M0,300 Q300,200 600,300 T1200,300 L1200,400 L0,400 Z" />
            </svg>

            <svg className="edu-icon edu-book" viewBox="0 0 100 100" aria-hidden="true">
              <path
                className="draw-path"
                d="M20,30 L20,80 L50,70 L80,80 L80,30 L50,40 Z M50,40 L50,70"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            <svg className="edu-icon edu-lightbulb" viewBox="0 0 100 100" aria-hidden="true">
              <path
                className="draw-path"
                d="M50,20 L50,15 M35,28 L31,24 M65,28 L69,24 M50,25 A15,15 0 0,1 65,40 A15,15 0 0,1 50,55 A15,15 0 0,1 35,40 A15,15 0 0,1 50,25 M42,55 L42,65 L58,65 L58,55 M45,65 L45,70 L55,70 L55,65 M48,70 L48,75 L52,75 L52,70"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            <svg className="edu-icon edu-rocket" viewBox="0 0 100 100" aria-hidden="true">
              <path
                className="draw-path"
                d="M50,15 L60,50 L70,60 L60,65 L50,85 L40,65 L30,60 L40,50 Z M35,65 L25,75 M65,65 L75,75 M45,50 A5,5 0 1,1 55,50 A5,5 0 1,1 45,50"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            <svg className="edu-icon edu-star" viewBox="0 0 100 100" aria-hidden="true">
              <path
                className="draw-path"
                d="M50,20 L58,45 L85,45 L63,60 L70,85 L50,70 L30,85 L37,60 L15,45 L42,45 Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            <svg className="edu-icon edu-atom" viewBox="0 0 100 100" aria-hidden="true">
              <ellipse
                className="draw-path"
                cx="50"
                cy="50"
                rx="35"
                ry="15"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              />
              <ellipse
                className="draw-path"
                cx="50"
                cy="50"
                rx="35"
                ry="15"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                transform="rotate(60 50 50)"
              />
              <ellipse
                className="draw-path"
                cx="50"
                cy="50"
                rx="35"
                ry="15"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                transform="rotate(120 50 50)"
              />
              <circle cx="50" cy="50" r="4" fill="currentColor" />
            </svg>

            <svg
              className="particle-connections"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <line className="connection-line connection-line-1" x1="20" y1="30" x2="80" y2="70" />
              <line className="connection-line connection-line-2" x1="40" y1="60" x2="70" y2="30" />
              <line className="connection-line connection-line-3" x1="30" y1="80" x2="60" y2="40" />
            </svg>
          </div>

          <div className="login-illustrations-scattered">
            <div className="scattered-illustration illustration-color-2 illus-pos-1 illus-mask-1" />
            <div className="scattered-illustration illustration-color-1 illus-pos-2 illus-mask-2" />
            <div className="scattered-illustration illustration-color-3 illus-pos-3 illus-mask-3" />
            <div className="scattered-illustration illustration-color-2 illus-pos-4 illus-mask-4" />
            <div className="scattered-illustration illustration-color-1 illus-pos-5 illus-mask-5" />
            <div className="scattered-illustration illustration-color-3 illus-pos-6 illus-mask-6" />
            <div className="scattered-illustration illustration-color-2 illus-pos-7 illus-mask-7" />
          </div>

          <AuthCard>
            {renderLoginView()}
            {renderResetView()}
            {renderSetupView()}
            {renderPhoneView()}
          </AuthCard>
        </div>

        {renderOtpModal()}

        <div id="recaptcha-container" />
      </div>
      <Toaster hasNavbar={false} />
    </>
  );
};
